import { useFormikContext } from "formik";
import { hasMilestoneComponent } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    MAX_RECOGNITION_COMPONENTS,
    resolveTermForMethod,
} from "@common/constants/domains/accounting/schedules";
import { hasTermRecognitionComponent } from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { PlayIcon, PlusIcon } from "@icons";
import { useTypedFlags } from "@hooks/useTypedFlags";
import classNames from "classnames";
import { ScheduleComponentRow } from "./ScheduleComponentRow";
import { ScheduleFormSection } from "./ScheduleFormSection";
import { ScheduleMilestonePlanEditor } from "./ScheduleMilestonePlanEditor";
import {
    addScheduleComponent,
    removeScheduleComponent,
    ScheduleAllocationState,
    ScheduleComponentForm,
    ScheduleTemplateFormValues,
    setScheduleComponentAllocation,
} from "./scheduleFormHelpers";
import { switchScheduleComponentMethod } from "./scheduleMilestoneFormHelpers";
import styles from "./ScheduleDrawer.module.scss";

export const ScheduleRecognitionSection: React.FC = () => {
    const { values, errors, submitCount, setFieldValue } =
        useFormikContext<ScheduleTemplateFormValues>();
    const { schedulesMilestoneBased: isMilestoneAvailable } = useTypedFlags();
    const componentsError =
        submitCount > 0 && typeof errors.components === "string"
            ? errors.components
            : null;
    const componentErrors =
        typeof errors.components === "string" ? undefined : errors.components;
    const getAllocationError = (index: number) => {
        if (submitCount === 0) {
            return undefined;
        }
        const componentError = componentErrors?.[index];
        if (componentError == null || typeof componentError === "string") {
            return componentError;
        }
        return typeof componentError.allocationPercent === "string"
            ? componentError.allocationPercent
            : undefined;
    };
    const termLengthError =
        submitCount > 0 && typeof errors.termLength === "string"
            ? errors.termLength
            : null;
    const termUnitError =
        submitCount > 0 && typeof errors.termUnit === "string"
            ? errors.termUnit
            : null;
    const hasTermMethod = hasTermRecognitionComponent(values.components);
    const isMilestone = hasMilestoneComponent(values.components);

    const applyAllocationState = (state: ScheduleAllocationState) => {
        setFieldValue("components", state.components);
        setFieldValue("allocationMode", state.allocationMode);
    };

    const updateComponent = (
        index: number,
        patch: Partial<ScheduleComponentForm>,
    ) => {
        const { method, ...rest } = patch;
        if (method != null) {
            const next = switchScheduleComponentMethod({
                values,
                index,
                method,
            });
            setFieldValue(
                "components",
                next.components.map((component, componentIndex) =>
                    componentIndex === index
                        ? { ...component, ...rest }
                        : component,
                ),
            );
            setFieldValue("milestones", next.milestones);
            const term = resolveTermForMethod({
                method,
                term: { length: values.termLength, unit: values.termUnit },
            });
            if (term.unit !== values.termUnit) {
                setFieldValue("termLength", term.length);
                setFieldValue("termUnit", term.unit);
            }
            return;
        }
        if (patch.allocationPercent != null) {
            applyAllocationState(
                setScheduleComponentAllocation({
                    components: values.components,
                    allocationMode: values.allocationMode,
                    index,
                    allocationPercent: patch.allocationPercent,
                }),
            );
            return;
        }
        setFieldValue(
            "components",
            values.components.map((component, componentIndex) =>
                componentIndex === index
                    ? { ...component, ...patch }
                    : component,
            ),
        );
    };

    return (
        <ScheduleFormSection
            title="Recognition"
            icon={<PlayIcon className="icon-color-blue-600" />}
            iconClassName={styles.sectionIconRecognition}
            sectionClassName={styles.sectionLast}
        >
            <div className={styles.card}>
                <div className={styles.cardStack}>
                    {(componentsError != null ||
                        termLengthError != null ||
                        termUnitError != null) && (
                        <div className={styles.cardRowFlush}>
                            {componentsError && (
                                <p className={styles.fieldError}>
                                    {componentsError}
                                </p>
                            )}
                            {termLengthError && (
                                <p className={styles.fieldError}>
                                    {termLengthError}
                                </p>
                            )}
                            {termUnitError && (
                                <p className={styles.fieldError}>
                                    {termUnitError}
                                </p>
                            )}
                        </div>
                    )}
                    {values.components.map((component, index) => (
                        <ScheduleComponentRow
                            key={component.key}
                            component={component}
                            hasTermMethod={hasTermMethod}
                            isMilestoneAvailable={isMilestoneAvailable}
                            canRemove={values.components.length > 1}
                            isOnlyComponent={values.components.length === 1}
                            isAllocationLocked={
                                values.components.length === 1 ||
                                index === values.components.length - 1
                            }
                            allocationError={getAllocationError(index)}
                            termLength={values.termLength}
                            termUnit={values.termUnit}
                            onComponentChange={(patch) =>
                                updateComponent(index, patch)
                            }
                            onRemove={() =>
                                applyAllocationState(
                                    removeScheduleComponent({
                                        components: values.components,
                                        allocationMode: values.allocationMode,
                                        index,
                                    }),
                                )
                            }
                            onTermLengthChange={(termLength) =>
                                setFieldValue("termLength", termLength)
                            }
                            onTermUnitChange={(termUnit) =>
                                setFieldValue("termUnit", termUnit)
                            }
                        />
                    ))}
                </div>
                {isMilestone && (
                    <div className={styles.cardRowTopDivider}>
                        <ScheduleMilestonePlanEditor />
                    </div>
                )}
                {!isMilestone &&
                    values.components.length < MAX_RECOGNITION_COMPONENTS && (
                        <div
                            className={classNames(
                                styles.cardRow,
                                styles.cardRowTopDivider,
                            )}
                        >
                            <Button
                                size="md"
                                type="button"
                                variant="primary"
                                onClick={() => {
                                    applyAllocationState(
                                        addScheduleComponent({
                                            components: values.components,
                                            allocationMode:
                                                values.allocationMode,
                                        }),
                                    );
                                }}
                                leftIcon={<PlusIcon />}
                            >
                                Add
                            </Button>
                        </div>
                    )}
            </div>
        </ScheduleFormSection>
    );
};
